<template>
  <el-config-provider>
    <router-view />
  </el-config-provider>
</template>

<script setup>
import { ElConfigProvider } from "element-plus";
import { onMounted, ref, watch } from "vue";
import { checkSession, getClientId } from "@/store/cache";
import { isChrome, isMobile } from "@/utils/libs";
import { showMessageInfo } from "@/utils/dialog";
import { useSharedStore } from "@/store/sharedata";
import { getUserToken } from "@/store/session";

const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const _ResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ResizeObserver {
  constructor(callback) {
    callback = debounce(callback, 200);
    super(callback);
  }
};

const store = useSharedStore();
onMounted(() => {
  if (!isChrome() && !isMobile()) {
    showMessageInfo("建议使用 Chrome 浏览器以获得最佳体验。");
  }

  // 如果有 token 且 store 中没有登录状态，才验证 session
  const hasToken = !!getUserToken();
  if (hasToken && !store.isLogin) {
    checkSession()
      .then((res) => {
        store.setIsLogin(true);
        store.setUserRole(res.role);
      })
      .catch(() => {
        // token 无效，清除状态
        store.clearAuth();
      });
  }
  
  // 如果已登录且 WebSocket 未连接，建立连接
  if (store.isLogin && !store.socket.conn) {
    connect();
  }
  
  // 设置主题
  document.documentElement.setAttribute("data-theme", store.theme);
});

watch(
  () => store.isLogin,
  (val) => {
    if (val) {
      connect();
    }
  }
);

const handler = ref(0);
// 初始化 websocket 连接
const connect = () => {
  const token = getUserToken();
  if (!token) return; // token 为空时跳过连接，避免 WebSocket subprotocol 异常

  let host = process.env.VUE_APP_WS_HOST;
  if (host === "") {
    if (location.protocol === "https:") {
      host = "wss://" + location.host;
    } else {
      host = "ws://" + location.host;
    }
  }
  const clientId = getClientId();
  const _socket = new WebSocket(host + `/api/ws?client_id=${clientId}`, [
    "token",
    token,
  ]);
  _socket.addEventListener("open", () => {
    console.log("WebSocket 已连接");
    handler.value = setInterval(() => {
      if (_socket.readyState === WebSocket.OPEN) {
        _socket.send(JSON.stringify({ type: "ping" }));
      }
    }, 5000);
  });
  _socket.addEventListener("close", () => {
    clearInterval(handler.value);
    connect();
  });
  store.setSocket(_socket);
};
</script>

<style lang="stylus">
html, body {
  margin: 0;
  padding: 0;
}

#app {
  margin: 0 !important;
  padding: 0 !important;
  font-family: Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  --primary-color: #21aa93;

  h1 { /* 通常是 2em */
    font-size: 2em;
  }

  /* 通常是 2em */
  h2 { /* 通常是 1.5em */
    font-size: 1.5em;
  }

  /* 通常是 1.5em */
  h3 { /* 通常是 1.17em */
    font-size: 1.17em;
  }

  /* 通常是 1.17em */
  h4 { /* 通常是 1em */
    font-size: 1em;
  }

  /* 通常是 1em */
  h5 { /* 通常是 0.83em */
    font-size: 0.83em;
  }

  /* 通常是 0.83em */
  h6 { /* 通常是 0.67em */
    font-size: 0.67em;
  }

  /* 通常是 0.67em */
}

.el-overlay-dialog {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  .el-dialog {
    margin: 0;

    .el-dialog__body {
      // max-height 80vh
      overflow-y: auto;
    }
  }
}

/* 省略显示 */
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.van-toast--fail {
  background: #fef0f0;
  color: #f56c6c;
}

.van-toast--success {
  background: #D6FBCC;
  color: #07C160;
}

@import '@/assets/iconfont/iconfont.css';
</style>
